import {
  type ComputedRef,
  computed,
  type MaybeRefOrGetter,
  toValue,
} from 'vue';
import {
  displayModeFromFlags,
  type GridLayout,
  type PortraitOrientation,
  pickGridLayout,
} from '@/shared/lib/pick-grid-layout';
import {
  portraitAspectRatioForOrientation,
  resolvePortraitOrientation,
} from '@/shared/lib/portrait-orientation';

interface KioskLike {
  portrait_orientation?: unknown;
}

interface UsePortraitGridLayoutOptions {
  kiosk: MaybeRefOrGetter<KioskLike | null | undefined>;
  isVerticalKiosk: MaybeRefOrGetter<boolean>;
  viewportIsPortrait: MaybeRefOrGetter<boolean>;
  portraitsPerSlide: number;
}

export interface PortraitGridLayoutState {
  displayPageSize: ComputedRef<number>;
  galleryLayout: ComputedRef<GridLayout>;
  portraitArrowScale: ComputedRef<number>;
  portraitCellAspect: ComputedRef<number>;
  portraitCellHeight: ComputedRef<string>;
  portraitCellRadius: ComputedRef<string>;
  portraitCellWidth: ComputedRef<string>;
  portraitFit: ComputedRef<string>;
  portraitGridGap: ComputedRef<string>;
  portraitGridSizing: ComputedRef<string>;
  portraitOrientation: ComputedRef<PortraitOrientation>;
  useImageGrid: ComputedRef<boolean>;
}

export function usePortraitGridLayout(
  options: UsePortraitGridLayoutOptions
): PortraitGridLayoutState {
  const portraitOrientation = computed(() =>
    resolvePortraitOrientation(toValue(options.kiosk))
  );
  const useImageGrid = computed(
    () =>
      toValue(options.isVerticalKiosk) &&
      portraitOrientation.value === 'horizontal'
  );
  const portraitCellAspect = computed(() =>
    useImageGrid.value
      ? portraitAspectRatioForOrientation(portraitOrientation.value)
      : 3 / 4
  );
  const portraitFit = computed(() =>
    useImageGrid.value ? 'contain' : 'cover'
  );
  const portraitGridSizing = computed(() => {
    if (toValue(options.isVerticalKiosk)) {
      return portraitOrientation.value === 'vertical' ? 'height-fixed' : 'fill';
    }

    return 'width-fixed';
  });
  const portraitCellWidth = computed(() => {
    if (
      toValue(options.viewportIsPortrait) &&
      !toValue(options.isVerticalKiosk)
    ) {
      return '100vw';
    }

    return portraitOrientation.value === 'horizontal' ? '38rem' : '26rem';
  });
  const galleryLayout = computed(() =>
    pickGridLayout(
      displayModeFromFlags(
        toValue(options.isVerticalKiosk),
        toValue(options.viewportIsPortrait)
      ),
      portraitOrientation.value,
      options.portraitsPerSlide
    )
  );
  const displayPageSize = computed(
    () => galleryLayout.value.columns * galleryLayout.value.rows
  );
  const verticalKioskPortraitCellHeight = computed(() => {
    const rows = Math.max(1, galleryLayout.value.rows);
    const rowGapTotal = `${(rows - 1) * 1.5}rem`;
    return `calc((100vh - var(--Kiosk--wrapper-top-gap, 10rem) - var(--Kiosk--wrapper-bottom-gap, 11rem) - 2rem - 4rem - ${rowGapTotal}) / ${rows})`;
  });
  const portraitCellHeight = computed(() => {
    if (
      toValue(options.isVerticalKiosk) &&
      portraitOrientation.value === 'vertical'
    ) {
      return verticalKioskPortraitCellHeight.value;
    }

    return '40rem';
  });
  const portraitGridGap = computed(() => {
    if (toValue(options.isVerticalKiosk)) {
      return '1.5rem';
    }

    if (toValue(options.viewportIsPortrait)) {
      return '0px';
    }

    return '2.7rem';
  });
  const portraitCellRadius = computed(() => {
    if (toValue(options.isVerticalKiosk)) {
      return '0.8rem';
    }

    if (toValue(options.viewportIsPortrait)) {
      return '0px';
    }

    return `calc(${portraitCellWidth.value} * 0.06 * 0.7)`;
  });
  const portraitArrowScale = computed(() => {
    if (toValue(options.isVerticalKiosk)) {
      return 0.12;
    }

    if (toValue(options.viewportIsPortrait)) {
      return 0.095;
    }

    return 0.175;
  });

  return {
    displayPageSize,
    galleryLayout,
    portraitArrowScale,
    portraitCellAspect,
    portraitCellHeight,
    portraitCellRadius,
    portraitCellWidth,
    portraitFit,
    portraitGridGap,
    portraitGridSizing,
    portraitOrientation,
    useImageGrid,
  };
}
